import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/components/admin/OrderManagement.jsx - Order Management for Admin
import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Card,
    CardContent,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Tooltip,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Divider,

    TimelineItem,
    TimelineSeparator,
    TimelineConnector,
    TimelineContent,
    TimelineOppositeContent,
    TimelineDot,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    Visibility as ViewIcon,
    Edit as EditIcon,
    LocalShipping as ShippingIcon,
    Payment as PaymentIcon,
    Receipt as ReceiptIcon,
    ExpandMore as ExpandMoreIcon,
    CheckCircle as CheckIcon,
    Cancel as CancelIcon,
    Inventory as InventoryIcon,
    TrackChanges as TrackIcon
} from '@mui/icons-material';

import { ecommerceApi, cartHelpers, orderHelpers } from '../../services/ecommerceApi';
import { useNotification } from '../common/GlobalNotifications';

const OrderManagement = ({ orders, onOrderUpdate }) => {
  const { t } = useTranslation();
    const { showSuccess, showError } = useNotification();

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [orderDetailOpen, setOrderDetailOpen] = useState(false);
    const [statusUpdateOpen, setStatusUpdateOpen] = useState(false);
    const [shippingUpdateOpen, setShippingUpdateOpen] = useState(false);

    // Status update form
    const [newStatus, setNewStatus] = useState('');
    const [adminNotes, setAdminNotes] = useState('');

    // Shipping update form
    const [shippingData, setShippingData] = useState({
        status: '',
        description: '',
        location: '',
        tracking_number: '',
        carrier: ''
    });

    const handleViewOrder = (order) => {
        setSelectedOrder(order);
        setOrderDetailOpen(true);
    };

    const handleStatusUpdate = (order) => {
        setSelectedOrder(order);
        setNewStatus(order.status);
        setAdminNotes('');
        setStatusUpdateOpen(true);
    };

    const handleShippingUpdate = (order) => {
        setSelectedOrder(order);
        setShippingData({
            status: order.shipping_status,
            description: '',
            location: '',
            tracking_number: order.tracking_number || '',
            carrier: order.carrier || ''
        });
        setShippingUpdateOpen(true);
    };

    const submitStatusUpdate = async () => {
        if (!selectedOrder || !newStatus) return;

        try {
            await ecommerceApi.admin.orders.updateStatus(
                selectedOrder.id,
                newStatus,
                adminNotes || null
            );

            showSuccess(t('hardcoded.components_admin_ordermanagement.estado_de_orden_actualizado'));
            setStatusUpdateOpen(false);
            onOrderUpdate && onOrderUpdate();
        } catch (error) {
            showError(error.response?.data?.detail || t('hardcoded.components_admin_ordermanagement.error_al_actualizar_estado'));
        }
    };

    const submitShippingUpdate = async () => {
        if (!selectedOrder || !shippingData.status || !shippingData.description) return;

        try {
            await ecommerceApi.admin.orders.addShippingUpdate(selectedOrder.id, {
                status: shippingData.status,
                description: shippingData.description,
                location: shippingData.location || null,
                tracking_number: shippingData.tracking_number || null,
                carrier: shippingData.carrier || null
            });

            showSuccess(t('hardcoded.components_admin_ordermanagement.actualizacion_de_envio_agregada'));
            setShippingUpdateOpen(false);
            onOrderUpdate && onOrderUpdate();
        } catch (error) {
            showError(error.response?.data?.detail || t('hardcoded.components_admin_ordermanagement.error_al_agregar_actualizacion_de'));
        }
    };

    const getStatusActions = (order) => {
        const actions = [];

        // Can always view order details
        actions.push(
            <Tooltip key="view" title={t('hardcoded.components_admin_ordermanagement.ver_detalles')}>
                <IconButton size="small" onClick={() => handleViewOrder(order)}>
                    <ViewIcon />
                </IconButton>
            </Tooltip>
        );

        // Can update status if not delivered/cancelled
        if (!['delivered', 'cancelled', 'refunded'].includes(order.status)) {
            actions.push(
                <Tooltip key="status" title={t('hardcoded.components_admin_ordermanagement.actualizar_estado')}>
                    <IconButton size="small" onClick={() => handleStatusUpdate(order)}>
                        <EditIcon />
                    </IconButton>
                </Tooltip>
            );
        }

        // Can add shipping updates if confirmed or processing
        if (['confirmed', 'processing', 'shipped'].includes(order.status)) {
            actions.push(
                <Tooltip key="shipping" title={t('hardcoded.components_admin_ordermanagement.actualizar_envio')}>
                    <IconButton size="small" onClick={() => handleShippingUpdate(order)}>
                        <ShippingIcon />
                    </IconButton>
                </Tooltip>
            );
        }

        return actions;
    };

    const filteredOrders = orders || [];
    const paginatedOrders = filteredOrders.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>
                {t('hardcoded.components_admin_ordermanagement.gestion_de_ordenes')}</Typography>

            {/* Orders Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('hardcoded.components_admin_ordermanagement.numero_de_orden')}</TableCell>
                            <TableCell>{i18n.t('hardcoded.components_admin_ordermanagement.cliente')}</TableCell>
                            <TableCell>Fecha</TableCell>
                            <TableCell>Items</TableCell>
                            <TableCell>{i18n.t('common.total')}</TableCell>
                            <TableCell>{i18n.t('gps.table.status')}</TableCell>
                            <TableCell>Pago</TableCell>
                            <TableCell>{t('hardcoded.components_admin_couponform.envio')}</TableCell>
                            <TableCell>{t('common.actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedOrders.map((order) => (
                            <TableRow key={order.id} hover>
                                <TableCell>
                                    <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                                        {order.order_number}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">
                                        {order.user?.name} {order.user?.last_name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {order.user?.email}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">
                                        {new Date(order.created_at).toLocaleDateString('es-MX')}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {new Date(order.created_at).toLocaleTimeString('es-MX')}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={`${order.item_count} items`}
                                        size="small"
                                        variant="outlined"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" fontWeight={600}>
                                        {cartHelpers.formatCurrency(order.total)}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={order.status_display}
                                        size="small"
                                        color={orderHelpers.getStatusColor(order.status)}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={order.payment_status}
                                        size="small"
                                        color={order.payment_status === 'paid' ? 'success' : 'warning'}
                                        icon={<PaymentIcon />}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={order.shipping_status}
                                        size="small"
                                        icon={<ShippingIcon />}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        {getStatusActions(order)}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={filteredOrders.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                }}
                labelRowsPerPage={t('hardcoded.components_admin_ordermanagement.ordenes_por_pagina')}
            />

            {/* Order Detail Dialog */}
            <Dialog open={orderDetailOpen} onClose={() => setOrderDetailOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    {t('hardcoded.components_admin_ordermanagement.detalles_de_orden')}{selectedOrder?.order_number}
                </DialogTitle>
                <DialogContent dividers>
                    {selectedOrder && (
                        <Grid container spacing={3}>
                            {/* Order Summary */}
                            <Grid item xs={12} md={6}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            {t('hardcoded.components_admin_ordermanagement.informacion_de_la_orden')}</Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography>{t('hardcoded.components_admin_ordermanagement.numero')}</Typography>
                                                <Typography fontWeight={600}>{selectedOrder.order_number}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography>Fecha:</Typography>
                                                <Typography>{new Date(selectedOrder.created_at).toLocaleString('es-MX')}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography>{i18n.t('hardcoded.components_admin_ordermanagement.estado')}</Typography>
                                                <Chip label={selectedOrder.status_display} color={orderHelpers.getStatusColor(selectedOrder.status)} size="small" />
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography>{i18n.t('hardcoded.components_admin_ordermanagement.pago')}</Typography>
                                                <Chip label={selectedOrder.payment_status} color={selectedOrder.payment_status === 'paid' ? 'success' : 'warning'} size="small" />
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography>{i18n.t('hardcoded.components_admin_ordermanagement.total')}</Typography>
                                                <Typography variant="h6" color="primary">{cartHelpers.formatCurrency(selectedOrder.total)}</Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Customer Info */}
                            <Grid item xs={12} md={6}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            {i18n.t('hardcoded.components_admin_ordermanagement.cliente')}</Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Typography>
                                                <strong>Nombre:</strong> {selectedOrder.user?.name} {selectedOrder.user?.last_name}
                                            </Typography>
                                            <Typography>
                                                <strong>Email:</strong> {selectedOrder.user?.email}
                                            </Typography>
                                            <Typography>
                                                <strong>{i18n.t('hardcoded.components_admin_ordermanagement.empresa')}</strong> {selectedOrder.company?.company_name}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Shipping Address */}
                            <Grid item xs={12} md={6}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            {t('hardcoded.components_admin_ordermanagement.direccion_de_envio')}</Typography>
                                        <Typography>
                                            {selectedOrder.shipping_address?.first_name} {selectedOrder.shipping_address?.last_name}
                                        </Typography>
                                        {selectedOrder.shipping_address?.company && (
                                            <Typography>{selectedOrder.shipping_address.company}</Typography>
                                        )}
                                        <Typography>{selectedOrder.shipping_address?.address_line_1}</Typography>
                                        {selectedOrder.shipping_address?.address_line_2 && (
                                            <Typography>{selectedOrder.shipping_address.address_line_2}</Typography>
                                        )}
                                        <Typography>
                                            {selectedOrder.shipping_address?.city}, {selectedOrder.shipping_address?.state} {selectedOrder.shipping_address?.postal_code}
                                        </Typography>
                                        <Typography>{selectedOrder.shipping_address?.country}</Typography>
                                        {selectedOrder.shipping_address?.phone && (
                                            <Typography>{i18n.t('hardcoded.components_admin_ordermanagement.tel')}{selectedOrder.shipping_address.phone}</Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Shipping Info */}
                            <Grid item xs={12} md={6}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            {t('hardcoded.components_admin_ordermanagement.informacion_de_envio')}</Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography>{i18n.t('hardcoded.components_admin_ordermanagement.estado')}</Typography>
                                                <Chip label={selectedOrder.shipping_status} size="small" />
                                            </Box>
                                            {selectedOrder.carrier && (
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography>{i18n.t('hardcoded.pages_orderspage.transportista')}</Typography>
                                                    <Typography>{selectedOrder.carrier}</Typography>
                                                </Box>
                                            )}
                                            {selectedOrder.tracking_number && (
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography>{t('hardcoded.pages_orderspage.numero_de_rastreo')}</Typography>
                                                    <Typography fontFamily="monospace">{selectedOrder.tracking_number}</Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Order Items */}
                            <Grid item xs={12}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            {i18n.t('hardcoded.components_admin_ordermanagement.productos_ordenados')}</Typography>
                                        <List>
                                            {selectedOrder.items?.map((item, index) => (
                                                <React.Fragment key={item.id}>
                                                    <ListItem>
                                                        <ListItemText
                                                            primary={item.name}
                                                            secondary={t('hardcoded.components_admin_ordermanagement.sku_sku_cantidad_quantity', { sku: item.sku, quantity: item.quantity })}
                                                        />
                                                        <Box sx={{ textAlign: 'right' }}>
                                                            <Typography variant="body2">
                                                                {cartHelpers.formatCurrency(item.unit_price)} c/u
                                                            </Typography>
                                                            <Typography variant="subtitle1" fontWeight={600}>
                                                                {cartHelpers.formatCurrency(item.total_price)}
                                                            </Typography>
                                                        </Box>
                                                    </ListItem>
                                                    {index < selectedOrder.items.length - 1 && <Divider />}
                                                </React.Fragment>
                                            ))}
                                        </List>

                                        <Divider sx={{ my: 2 }} />

                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography>{i18n.t('hardcoded.components_admin_kitform.subtotal')}</Typography>
                                                <Typography>{cartHelpers.formatCurrency(selectedOrder.subtotal)}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography>{i18n.t('hardcoded.components_admin_ordermanagement.iva')}</Typography>
                                                <Typography>{cartHelpers.formatCurrency(selectedOrder.tax_amount)}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography>{t('hardcoded.components_admin_ordermanagement.envio')}</Typography>
                                                <Typography>{cartHelpers.formatCurrency(selectedOrder.shipping_amount)}</Typography>
                                            </Box>
                                            {selectedOrder.discount_amount > 0 && (
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography color="success.main">{i18n.t('hardcoded.components_admin_ordermanagement.descuento')}</Typography>
                                                    <Typography color="success.main">-{cartHelpers.formatCurrency(selectedOrder.discount_amount)}</Typography>
                                                </Box>
                                            )}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                                                <Typography variant="h6">{i18n.t('hardcoded.components_admin_ordermanagement.total')}</Typography>
                                                <Typography variant="h6" color="primary">{cartHelpers.formatCurrency(selectedOrder.total)}</Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Notes */}
                            {(selectedOrder.customer_notes || selectedOrder.admin_notes) && (
                                <Grid item xs={12}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                {i18n.t('hardcoded.components_admin_ordermanagement.notas')}</Typography>
                                            {selectedOrder.customer_notes && (
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2">{t('hardcoded.components_admin_ordermanagement.notas_del_cliente')}</Typography>
                                                    <Typography variant="body2">{selectedOrder.customer_notes}</Typography>
                                                </Box>
                                            )}
                                            {selectedOrder.admin_notes && (
                                                <Box>
                                                    <Typography variant="subtitle2">Notas administrativas:</Typography>
                                                    <Typography variant="body2">{selectedOrder.admin_notes}</Typography>
                                                </Box>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOrderDetailOpen(false)}>{t('common.close')}</Button>
                </DialogActions>
            </Dialog>

            {/* Status Update Dialog */}
            <Dialog open={statusUpdateOpen} onClose={() => setStatusUpdateOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('hardcoded.components_admin_ordermanagement.actualizar_estado_de_orden')}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>{i18n.t('hardcoded.components_admin_ordermanagement.nuevo_estado')}</InputLabel>
                                <Select
                                    value={newStatus}
                                    label={t('hardcoded.components_admin_ordermanagement.nuevo_estado')}
                                    onChange={(e) => setNewStatus(e.target.value)}
                                >
                                    <MenuItem value="pending">{i18n.t('hardcoded.components_admin_ordermanagement.pendiente')}</MenuItem>
                                    <MenuItem value="confirmed">{i18n.t('navbar.confirmed')}</MenuItem>
                                    <MenuItem value="processing">{i18n.t('hardcoded.components_admin_ordermanagement.procesando')}</MenuItem>
                                    <MenuItem value="shipped">{i18n.t('hardcoded.components_admin_ordermanagement.enviada')}</MenuItem>
                                    <MenuItem value="delivered">{i18n.t('hardcoded.components_admin_ordermanagement.entregada')}</MenuItem>
                                    <MenuItem value="cancelled">{i18n.t('hardcoded.components_admin_ordermanagement.cancelada')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={t('hardcoded.components_admin_ordermanagement.notas_administrativas')}
                                value={adminNotes}
                                onChange={(e) => setAdminNotes(e.target.value)}
                                multiline
                                rows={3}
                                placeholder={t('hardcoded.components_admin_ordermanagement.agregar_notas_sobre_el_cambio')}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStatusUpdateOpen(false)}>{t('common.cancel')}</Button>
                    <Button onClick={submitStatusUpdate} variant="contained">
                        {i18n.t('hardcoded.components_admin_ordermanagement.actualizar_estado')}</Button>
                </DialogActions>
            </Dialog>

            {/* Shipping Update Dialog */}
            <Dialog open={shippingUpdateOpen} onClose={() => setShippingUpdateOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('hardcoded.components_admin_ordermanagement.agregar_actualizacion_de_envio')}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>{t('hardcoded.components_admin_ordermanagement.estado_de_envio')}</InputLabel>
                                <Select
                                    value={shippingData.status}
                                    label={t('hardcoded.components_admin_ordermanagement.estado_de_envio')}
                                    onChange={(e) => setShippingData(prev => ({ ...prev, status: e.target.value }))}
                                >
                                    <MenuItem value="preparing">{i18n.t('hardcoded.components_admin_ordermanagement.preparando')}</MenuItem>
                                    <MenuItem value="shipped">{i18n.t('hardcoded.components_admin_ordermanagement.enviado')}</MenuItem>
                                    <MenuItem value="in_transit">{t('hardcoded.components_admin_ordermanagement.en_transito')}</MenuItem>
                                    <MenuItem value="delivered">{i18n.t('hardcoded.components_admin_ordermanagement.entregado')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={t('hardcoded.components_admin_ordermanagement.descripcion')}
                                value={shippingData.description}
                                onChange={(e) => setShippingData(prev => ({ ...prev, description: e.target.value }))}
                                placeholder={t('hardcoded.components_admin_ordermanagement.ej_paquete_enviado_desde_almacen')}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={t('hardcoded.components_admin_ordermanagement.ubicacion')}
                                value={shippingData.location}
                                onChange={(e) => setShippingData(prev => ({ ...prev, location: e.target.value }))}
                                placeholder={t('hardcoded.components_admin_ordermanagement.ej_ciudad_de_mexico_cdmx')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t('hardcoded.components_admin_ordermanagement.numero_de_rastreo')}
                                value={shippingData.tracking_number}
                                onChange={(e) => setShippingData(prev => ({ ...prev, tracking_number: e.target.value }))}
                                placeholder="Ej: 1234567890"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t('hardcoded.components_admin_ordermanagement.transportista')}
                                value={shippingData.carrier}
                                onChange={(e) => setShippingData(prev => ({ ...prev, carrier: e.target.value }))}
                                placeholder={t('hardcoded.components_admin_ordermanagement.ej_fedex_dhl_estafeta')}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShippingUpdateOpen(false)}>{t('common.cancel')}</Button>
                    <Button
                        onClick={submitShippingUpdate}
                        variant="contained"
                        disabled={!shippingData.status || !shippingData.description}
                    >
                        {t('hardcoded.components_admin_ordermanagement.agregar_actualizacion')}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default OrderManagement;